import { FormEvent, useState } from 'react';
import { Form, Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';

import { validatePassword as vp } from '../GlobalFunctions';
import { userChangePassword } from '../UserManagementHelpers';

import { SpinnerOverlay } from '../SpinnerOverlay';
import PrimaryButton from '../components/PrimaryButton/PrimaryButton';
import TextInput from '../components/TextInput';

import styles from './AccountSettingsForms.module.scss';
import { AuthError } from '@aws-amplify/auth/lib-esm/Errors';

interface PasswordFormValues {
  currentPassword: string;
  newPassword: string;
  newPassword2: string;
}

const AccountSettingsPasswordSchema = Yup.object().shape({
  newPassword: Yup.string().test('passwordValidator', function (value: string | undefined) {
    if (value) {
      const result = vp(value);
      return result === undefined ? true : this.createError({ message: result });
    }
    return true;
  }),
  newPassword2: Yup.string()
    .oneOf([Yup.ref('newPassword'), null], 'Passwords must match')
    .required('Please confirm new password'),
});

const ChangePassword = () => {
  const [isShowSpinner, setShowSpinner] = useState<boolean>(false);
  const [isShowSuccess, setShowSuccess] = useState<boolean>(false);
  const [isShowError, setShowError] = useState<boolean>(false);
  const [apiResponse, setApiResponse] = useState<string>('');

  const initialPasswordValues: PasswordFormValues = {
    currentPassword: '',
    newPassword: '',
    newPassword2: '',
  };

  const handleSubmit = async (values: PasswordFormValues, { setSubmitting }: FormikHelpers<PasswordFormValues>) => {
    setShowSpinner(true);
    try {
      const result = await userChangePassword(values.currentPassword, values.newPassword);
      console.info(`result = ${JSON.stringify(result, null, 2)}`);
      setShowSuccess(true);
    } catch (error: unknown) {
      console.info(`error is ${JSON.stringify(error, null, 2)}`);
      if (error instanceof AuthError) {
        if (error.name === 'NotAuthorizedException' && error.message === 'Incorrect username or password.') {
          setApiResponse(`The current password you have supplied is incorrect`);
        } else {
          setApiResponse(error.message);
        }
      } else {
        setApiResponse((error as Error).toString());
      }

      setShowError(true);
    } finally {
      setSubmitting(false);
      setShowSpinner(false);
    }
  };

  return (
    <section className={styles.container}>
      <SpinnerOverlay isShow={isShowSpinner} />
      <Formik
        initialValues={initialPasswordValues}
        onSubmit={handleSubmit}
        validationSchema={AccountSettingsPasswordSchema}
      >
        {({ handleChange, setFieldTouched }) => (
          <Form>
            <div className={styles.halfRow}>
              <TextInput label='Enter Current Password' name='currentPassword' type='password' placeholder='' />
            </div>
            <div className={styles.halfRow}>
              <TextInput
                label='New Password'
                name='newPassword'
                type='password'
                placeholder=''
                onChange={(e: FormEvent<HTMLInputElement>) => {
                  setFieldTouched('newPassword');
                  handleChange(e);
                }}
              />
            </div>
            <div className={styles.halfRow}>
              <TextInput
                label='Confirm New Password'
                name='newPassword2'
                type='password'
                placeholder=''
                onChange={(e: FormEvent<HTMLInputElement>) => {
                  setFieldTouched('newPassword2');
                  handleChange(e);
                }}
              />
            </div>
            <div className={styles.buttonRow}>
              <PrimaryButton small type='submit' className={styles.updateButton}>
                Update Password
              </PrimaryButton>
              <div
                className={isShowSuccess ? styles.successShown : styles.successHidden}
                onTransitionEnd={() => setShowSuccess(false)}
              >
                You have successfully updated your password.
              </div>
              <div
                className={isShowError ? styles.errorShown : styles.errorHidden}
                onTransitionEnd={() => setShowError(false)}
              >
                {apiResponse}
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </section>
  );
};

export default ChangePassword;
